<template>
  <div class="modal-overlay">
    <div class="modal-content-wrapper">
      <div class="modal-header">
        Add Part
        <CloseSvg
          class="close"
          :dark="true"
          @click="onCloseClicked"
        />
      </div>
      <div class="modal-content">
        <TextInput
          v-if="inventoryItem"
          v-model="inventoryItem.name"
          :disabled="true"
          label="Inventory Item"
        />
        <NumericInput
          v-if="inventoryItem"
          v-model="quantity"
          label="Quantity"
          class="input"
        />
        <TextArea
          v-if="inventoryItem"
          v-model="note"
          label="Note"
          class="input"
        />
        <Checkbox
          v-if="inventoryItem"
          v-model="customerVisible"
          label="Visible to customer?"
        />
      </div>
      <div class="modal-footer">
        <LockingButton
          class="save-button"
          :disabled="!isFormValid"
          :locked="saving"
          :text="'Save'"
          @click="onSaveClicked"
        />
      </div>
    </div>
    <SelectPartModal
      v-if="!inventoryItem"
      @close="onCloseClicked"
      @inventory-item="onInventoryItemSelected"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { QuoteService } from '@/services/quote.service'
import { InventoryItem } from '@/@types/responses/inventory_item/InventoryItem'
import { OrderService } from '@/services/order.service'
import { CreateOrderPartMessage } from '@/@types/messages/CreateOrderPartMessage'
import { CreateQuotePartMessage } from '@/@types/messages/CreateQuotePartMessage'
import NumericInput from '@/components/shared/NumericInput.vue'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import SelectPartModal from '@/components/modals/SelectPartModal.vue'
import { InventoryItemDetails } from '@/@types/responses/inventory_item/InventoryItemDetails'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'AddPartModal',
  components: {
    Checkbox,
    CloseSvg,
    LockingButton,
    NumericInput,
    SelectPartModal,
    TextArea,
    TextInput
  },
  props: {
    orderId: {
      type: Number,
      default: undefined
    },
    quoteId: {
      type: Number,
      default: undefined
    }
  },
  emits: ['close', 'part-added'],
  setup() {
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      orderService,
      quoteService,
      toast
    }
  },
  data() {
    let inventoryItem: InventoryItemDetails|undefined

    return {
      customerVisible: false,
      inventoryItemId: 0,
      inventoryItem,
      note: '',
      quantity: 1,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return this.inventoryItemId !== 0 && this.quantity > 0
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onInventoryItemSelected(inventoryItem: InventoryItem): void {
      this.inventoryItemId = inventoryItem.id
      this.inventoryItem = new InventoryItemDetails(inventoryItem.id, inventoryItem.name)
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      if (this.orderId !== undefined) {
        this.saving = true
        const createOrderPartMessage = new CreateOrderPartMessage(this.customerVisible, this.inventoryItemId, this.note, this.quantity)
        this.orderService.createOrderPart(this.orderId, createOrderPartMessage).then(() => {
          this.$emit('part-added')
          this.toast.success('Part added to order successfully.')
          this.customerVisible = false
          this.inventoryItemId = 0
          this.inventoryItem = undefined
          this.note = ''
          this.quantity = 1
          this.saving = false
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to add the part to the order. Please try again.')
          }
        })
      } else if (this.quoteId !== undefined) {
        this.saving = true
        const createQuotePartMessage = new CreateQuotePartMessage(this.customerVisible, this.inventoryItemId, this.note, this.quantity)
        this.quoteService.createQuotePart(this.quoteId, createQuotePartMessage).then(() => {
          this.$emit('part-added')
          this.toast.success('Part added to quote successfully.')
          this.customerVisible = false
          this.inventoryItemId = 0
          this.inventoryItem = undefined
          this.note = ''
          this.quantity = 1
          this.saving = false
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to add the part to the quote. Please try again.')
          }
        })
      }
    }
  }
})
</script>

<style scoped lang="scss">
.modal-overlay {
  @include modal-styles;
}

.modal-overlay > .modal-content-wrapper {
  > .modal-content {
    > .input {
      margin-top: 10px;
    }
  }
}
</style>